<template>
	<div
		class="bg-banner relative rounded-[12px] bg-o-75 bg-cover bg-right-top px-24px pt-60px mdp:px-32px mdp:pt-32px mdl:px-48px mdl:pt-48px lg:px-60px"
		:class="{ hidden: props.isMobile && props.isFocus }"
	>
		<div>
			<div
				v-if="isShowCommunityProfile"
				class="absolute left-1/2 top-0 flex -translate-x-1/2 cursor-pointer gap-4px whitespace-nowrap rounded-b-[12px] bg-o-100-75% px-16px py-[10px] transition-all mdp:left-auto mdp:right-32px mdp:transform-none mdl:right-48px lg:right-60px"
				@click="goToCommunityProfile"
			>
				<span class="typography-l2-emp">
					{{ textContent.button_community }}
				</span>
				<BaseIcon name="arrow-goto" class="block h-16px w-16px" />
			</div>
		</div>
		<div
			class="mdp:mb-24px mdp:flex mdp:items-end mdp:justify-between mdp:gap-20px lg:mb-32px"
		>
			<div
				class="mb-24px text-center mdp:mb-0 mdp:flex-initial mdp:text-left"
				:class="{ hidden: props.isMobile && props.isFocus }"
			>
				<h3 class="typography-h2-str mb-8px lg:mb-12px">
					<span class="text-n-950">{{ textContent.banner_title }}</span>
					<span>&nbsp;</span>
					<span class="text-o-500">
						{{ textContent.banner_title_highlight }}
					</span>
				</h3>
				<p class="typography-b2-reg text-n-950">
					{{ textContent.banner_description }}
				</p>
			</div>
			<div
				class="mb-24px flex justify-between gap-8px mdp:mb-0 mdp:flex-initial lg:gap-12px"
			>
				<div class="flex-auto mdp:w-[240px] mdl:w-[320px]">
					<slot name="search"></slot>
				</div>
				<div class="flex-grow-0">
					<slot name="filter"></slot>
				</div>
			</div>
		</div>
		<div class="flex justify-center gap-8px mdp:justify-start">
			<div
				v-for="tab in tabs"
				:key="tab.key"
				class="banner-tab-rounded relative flex cursor-pointer items-center justify-between gap-8px rounded-t-lg bg-transparent px-20px py-[14px] mdp:px-24px mdp:py-16px"
				:class="currentTabClass(tab.key)"
				@click="updateTab(tab.key)"
			>
				<div class="flex">
					<BaseIcon
						v-show="currentTab === tab.key"
						class="h-[22px] w-[22px]"
						:name="tab.iconFill"
					/>
					<BaseIcon
						v-show="currentTab !== tab.key"
						:name="tab.icon"
						class="h-[22px] w-[22px]"
					/>
				</div>
				<div class="typography-s1-emp">{{ tab.label }}</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { capitalizeFirstLetter } from '@/lib/utils'

interface TextContent {
	banner_title: string
	banner_title_highlight: string
	banner_description: string
	button_community: string
}

interface Tab {
	key: string
	label: string
	icon: string
	iconFill: string
}

const props = defineProps({
	textContent: {
		type: Object as PropType<TextContent>,
		required: true,
	},
	currentTab: {
		type: String,
		required: true,
	},
	isShowCommunityProfile: {
		type: Boolean,
		required: true,
	},
	tabs: {
		type: Array as PropType<Tab[]>,
		required: true,
	},
	isFocus: {
		type: Boolean,
		required: true,
	},
	isMobile: {
		type: Boolean,
		required: true,
	},
	currentBannerTab: {
		type: String,
		default: '',
	},
	currentSecondaryTab: {
		type: String,
		default: '',
	},
	currentCategoryAlias: {
		type: String,
		default: '',
	},
	currentWarehouseAlias: {
		type: String,
		default: '',
	},
	sortMode: {
		type: String,
	},
	currentSearchParameter: {
		type: String,
	},
})

const emit = defineEmits(['update:currentTab', 'goToCommunityProfile'])

const currentTabClass = computed(() => (tab: string) => {
	return props.currentTab === tab
		? 'bg-white before:opacity-100 after:opacity-100'
		: 'before:opacity-0 after:opacity-0'
})

function updateTab(key: string) {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Primary Tab',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: `${capitalizeFirstLetter(props.sortMode ?? '')}`,
		warehouse_filter: `${props.currentWarehouseAlias}`,
		category_filter: `${props.currentCategoryAlias}`,
		keyword: `${props.currentSearchParameter}`,
	})

	emit('update:currentTab', key)
}

function goToCommunityProfile() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Community Profile',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: `${capitalizeFirstLetter(props.sortMode ?? '')}`,
		warehouse_filter: `${props.currentWarehouseAlias}`,
		category_filter: `${props.currentCategoryAlias}`,
		keyword: `${props.currentSearchParameter}`,
	})
	emit('goToCommunityProfile')
}
</script>
